import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
/**
 * trust-row — a calm row of reassurance items (livraison, paiement à la livraison, retours, garantie).
 * Each item is a repeatable `item` BLOCK: an icon + a title + an optional subtitle. The Layout group
 * controls the column count (2–4) and the horizontal alignment; the shared "Section" universal group
 * controls scheme / padding / width. Seeds 4 default items so a fresh section is immediately useful.
 */
export const trustRowSchema = {
    type: 'trust-row',
    name: 'Trust row',
    version: 1,
    templates: ['product', 'index', 'collection', 'page', 'thank-you'],
    fields: [
        // ── Layout ──────────────────────────────────────────────────────────────
        {
            id: 'columns',
            type: 'select',
            label: 'Columns',
            group: 'Layout',
            info: 'How many items per row on desktop (wraps responsively on narrow screens).',
            default: '4',
            options: [
                { value: '2', label: '2 columns' },
                { value: '3', label: '3 columns' },
                { value: '4', label: '4 columns' },
            ],
        },
        {
            id: 'alignment',
            type: 'select',
            label: 'Alignment',
            group: 'Layout',
            default: 'center',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Center' },
            ],
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // ── Section (universal) ──────────────────────────────────────────────────
        ...universalFields,
    ],
    blocks: [
        {
            type: 'item',
            name: 'Item',
            max: 6,
            fields: [
                {
                    id: 'icon',
                    type: 'icon-name',
                    label: 'Icon',
                    default: 'local_shipping',
                    placeholder: 'check_circle',
                    info: 'Type a Google Material Symbols name (e.g. local_shipping, payments, verified_user). Search names on fonts.google.com/icons. Upload a custom image below to override.',
                },
                {
                    id: 'filledIcon',
                    type: 'checkbox',
                    label: 'Filled icon',
                    default: false,
                    info: 'Renders the solid (filled) variant of the icon.',
                },
                { id: 'image', type: 'image', label: 'Custom icon image' },
                {
                    id: 'title',
                    type: 'text',
                    label: 'Title',
                    default: 'Réassurance',
                    defaultKey: 'trust.section_title',
                    // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
                    aiWritable: true,
                    maxLength: 48,
                },
                { id: 'subtitle', type: 'text', label: 'Subtitle', default: '', maxLength: 64 },
            ],
        },
    ],
    maxBlocks: 6,
};
export const trustRowPresets = [
    {
        name: 'Trust row',
        // A calm LIGHT reassurance strip on the page background (default). The seller can switch it to the
        // accent/inverse band from the Section → Color scheme control if they want a coloured band.
        settings: {
            columns: '4',
            alignment: 'center',
            colorScheme: 'light',
            paddingTop: 44,
            paddingBottom: 44,
        },
        blocks: [
            {
                type: 'item',
                settings: {
                    icon: 'local_shipping',
                    title: '@t:trust.fast_shipping',
                    subtitle: '@t:trust.fast_shipping_sub',
                },
            },
            {
                type: 'item',
                settings: {
                    icon: 'payments',
                    title: '@t:checkout.pay_on_delivery',
                    subtitle: '@t:trust.cod_sub',
                },
            },
            {
                type: 'item',
                settings: {
                    icon: 'assignment_return',
                    title: '@t:trust.easy_returns',
                    subtitle: '@t:trust.easy_returns_sub',
                },
            },
            {
                type: 'item',
                settings: {
                    icon: 'verified_user',
                    title: '@t:trust.warranty',
                    subtitle: '@t:trust.warranty_sub',
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const trustRowMeta = {
    label: 'Trust row',
    group: 'Product',
    icon: 'shield-check',
};
//# sourceMappingURL=schema.js.map